"use client"

import { useState, useEffect, useMemo, useContext } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import {
  ChevronRight,
  X,
  Grid,
  User,
  Users,
  Clipboard,
  Shield,
  Headphones,
  Bell,
  BarChart2,
  LogOut,
  AlertCircle,
  ChartLine,
  ContactRound,
  Briefcase,
} from "lucide-react"
import styles from "./sidebar.module.css"
import VisaLogo from "../../assest/svg/visalogo.svg"
import { getNotifications } from "../../service/notification"
import { AuthContext } from "../../context/AuthContext"
import { invalidateCompanyProfile } from "../../service/authService"
import { useOrdersMenuLabel } from "../../hooks/useOrdersMenuLabel"
import { useTranslation } from "react-i18next"

export default function Sidebar() {
  const { t } = useTranslation()
  const { user } = useContext(AuthContext)
  const location = useLocation()
  const navigate = useNavigate()
  const [expandedMenus, setExpandedMenus] = useState(() => {
    const savedExpandedMenus = localStorage.getItem("expandedMenus")
    return savedExpandedMenus ? JSON.parse(savedExpandedMenus) : {}
  })
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [hoverIndex, setHoverIndex] = useState(null)
  const [activeItem, setActiveItem] = useState("dashboard")
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [notificationCount, setNotificationCount] = useState(() => Number(localStorage.getItem('notificationCount')) || 0)

  const [userData, setUserData] = useState({
    companyLogo: "",
    companyName: "",
  })
  const ordersMenuLabel = useOrdersMenuLabel()

  const fetchNotificationCount = async () => {
    try {
      const response = await getNotifications()

      if (response.status && response.data) {
        const unreadCount = typeof response.unread_count === 'number'
          ? response.unread_count
          : response.data.filter((notification) => notification.read_at === null).length
        setNotificationCount(unreadCount)
      }
    } catch (err) {
      console.error("Error fetching notifications:", err)
    }
  }

  useEffect(() => {
    if (user?.company) {
      setUserData({
        companyLogo: user.company.logo || "",
        companyName: user.company.name || "",
      })
    }
  }, [user])

  useEffect(() => {
    const initialDelay = setTimeout(() => fetchNotificationCount(), 2000)

    const interval = setInterval(() => fetchNotificationCount(), 5 * 60 * 1000)

    return () => {
      clearTimeout(initialDelay)
      clearInterval(interval)
    }
  }, [])

  useEffect(() => {
    localStorage.setItem('notificationCount', notificationCount)
    window.dispatchEvent(new CustomEvent('notificationUpdate', { detail: { count: notificationCount } }))
  }, [notificationCount])

  useEffect(() => {
    const handler = (e) => setNotificationCount(e.detail.count)
    window.addEventListener('notificationUpdate', handler)
    return () => window.removeEventListener('notificationUpdate', handler)
  }, [])

  const menuItems = useMemo(() => [
    {
      name: t("sidebar.dashboard"),
      key: "dashboard",
      icon: <Grid size={18} />,
      href: "/",
      active: false,
      hasSubmenu: false,
    },
    {
      name: t("sidebar.profile"),
      key: "profile",
      icon: <User size={18} />,
      href: "/profile",
      active: false,
      hasSubmenu: false,
    },
    {
      name: t("sidebar.admins"),
      key: "admins",
      icon: <Users size={18} />,
      href: "/admins",
      active: false,
      hasSubmenu: false,
    },
    {
      name: t("sidebar.employees"),
      key: "employees",
      icon: <ContactRound size={18} />,
      href: "/musteriler",
      active: false,
      hasSubmenu: false,
    },
    {
      name: ordersMenuLabel,
      icon: <Clipboard size={18} />,
      href: "/orders",
      active: false,
      // hasSubmenu: true,
      // submenuItems: [
      //   { name: "Yeni sifariş yarat", href: "/allorders" },
      //   { name: "Testlər", href: "/sifarisler/tamamlanan" },
      // ],
    },
    {
      name: t("sidebar.permissions"),
      icon: <Shield size={18} />,
      href: "/permissions",
      active: false,
      hasSubmenu: false,
    },
    {
      name: t("sidebar.supportTeam"),
      icon: <Headphones size={18} />,
      href: "/supportteam",
      active: false,
      hasSubmenu: false,
    },
    {
      name: t("sidebar.notifications"),
      icon: <Bell size={18} />,
      href: "/notification",
      active: false,
      hasSubmenu: false,
      badge: notificationCount > 0 ? notificationCount : null,
    },
    {
      name: t("sidebar.statistics"),
      icon: <ChartLine size={18} />,
      href: "/statistics",
      active: false,
      hasSubmenu: false,
    },
    {
      name: t("sidebar.activities"),
      icon: <BarChart2 size={18} />,
      href: "/activities",
      active: false,
      hasSubmenu: false,
    },
    {
      name: "CV Bazası",
      icon: <Briefcase size={18} />,
      href: "/cv-bazasi",
      active: false,
      hasSubmenu: false,
    },
    // {
    //   name: "Testlər",
    //   icon: <Briefcase size={18} />,
    //   href: "/teskilat",
    //   active: false,
    //   // hasSubmenu: true,
    //   // submenuItems: [
    //   //   { name: "Şirkət Məlumatları", href: "/teskilat/sirket" },
    //   //   { name: "Şöbələr", href: "/teskilat/sobeler" },
    //   //   { name: "İşçilər", href: "/teskilat/isciler" },
    //   // ],
    // },
  ], [ordersMenuLabel, notificationCount, t])

  useEffect(() => {
    const pathname = location.pathname

    let foundActiveItem = false

    for (const item of menuItems) {
      if (item.href === pathname) {
        setActiveItem(item.key || item.name)
        foundActiveItem = true
        break
      }

      if (item.hasSubmenu && item.submenuItems) {
        for (const subItem of item.submenuItems) {
          if (subItem.href === pathname) {
            setActiveItem(item.key || item.name)
            setExpandedMenus((prev) => ({ ...prev, [item.name]: true }))
            foundActiveItem = true
            break
          }
        }
        if (foundActiveItem) break
      }
    }

    if (!foundActiveItem && pathname === "/") {
      const dashboardItem = menuItems.find(item => item.href === "/")
      if (dashboardItem) setActiveItem(dashboardItem.key || dashboardItem.name)
    }
  }, [location.pathname, menuItems])

  useEffect(() => {
    localStorage.setItem("expandedMenus", JSON.stringify(expandedMenus))
  }, [expandedMenus])

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768
      setIsMobile(mobile)
      setSidebarOpen(!mobile)
    }

    handleResize()

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event) => {
      const logoutPopup = document.getElementById("logout-popup")
      const logoutButton = document.getElementById("logout-button")

      if (
        showLogoutConfirm &&
        logoutPopup &&
        !logoutPopup.contains(event.target) &&
        logoutButton &&
        !logoutButton.contains(event.target)
      ) {
        setShowLogoutConfirm(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showLogoutConfirm])

  const toggleMenu = (menuName, e) => {
    if (
      e.target.closest(`.${styles.chevronContainer}`) ||
      e.target.closest(`.${styles.chevronBox}`) ||
      e.target.closest(`.${styles.chevron}`)
    ) {
      e.preventDefault()
      e.stopPropagation()
      setExpandedMenus((prev) => ({
        ...prev,
        [menuName]: !prev[menuName],
      }))
    }
  }

  const toggleSidebar = () => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen)
    }
  }

  const handleMenuItemClick = (item, e) => {
    setActiveItem(item.key || item.name)

    if (
      e.target.closest(`.${styles.chevronContainer}`) ||
      e.target.closest(`.${styles.chevronBox}`) ||
      e.target.closest(`.${styles.chevron}`)
    ) {
      e.preventDefault()
      e.stopPropagation()

      if (item.hasSubmenu) {
        setExpandedMenus((prev) => ({
          ...prev,
          [item.name]: !prev[item.name],
        }))
      }
      return
    }

    if (item.href) {
      navigate(item.href)
    }

    if (isMobile && !item.hasSubmenu) {
      setSidebarOpen(false)
    }
  }

  const handleLogoutClick = (e) => {
    e.preventDefault()
    setShowLogoutConfirm(true)
  }

  const handleConfirmLogout = () => {
    invalidateCompanyProfile()
    localStorage.removeItem("token")
    localStorage.removeItem("isLoggedIn")

    localStorage.removeItem("expandedMenus")
    sessionStorage.clear()

    sessionStorage.setItem("loggedOut", "true")

    setShowLogoutConfirm(false)

    window.location.href = "/loginform"

    setTimeout(() => {
      window.location.reload(true)
    }, 100)
  }

  const handleCancelLogout = () => {
    setShowLogoutConfirm(false)
  }

  return (
    <>
      {isMobile && !sidebarOpen && (
        <div className={styles.mobileIndicator} onClick={toggleSidebar}>
          <div className={styles.verticalLine}></div>
          <div className={styles.arrowContainer}>
            <ChevronRight size={20} className={styles.arrowIcon} />
          </div>
        </div>
      )}

      <div
        className={`
          ${styles.sidebar} 
          ${isMobile ? styles.mobileSidebar : styles.desktopSidebar} 
          ${isMobile && !sidebarOpen ? styles.sidebarClosed : ""}
        `}
      >
        <div className={styles.sidebarBg}>
          <div className={styles.bgPattern}></div>
          <div className={styles.bgGlow}></div>
        </div>

        <div className={styles.sidebarHeader}>
          <div className={styles.logoFull}>
            <div className={styles.visaLogo}>
              {userData.companyLogo ? (
                <img
                  src={userData.companyLogo || "/placeholder.svg"}
                  alt={userData.companyName ? `${userData.companyName} Logo` : "Company Logo"}
                  onError={(e) => {
                    e.target.src = VisaLogo
                    e.target.alt = "Default Logo"
                  }}
                />
              ) : (
                <img src={VisaLogo || "/placeholder.svg"} alt="Default Logo" />
              )}
            </div>
          </div>
        </div>

        <div className={styles.sidebarContent}>
          <ul className={styles.menuList}>
            {menuItems.map((item, index) => (
              <li
                key={index}
                className={`${styles.menuItem} ${activeItem === (item.key || item.name) ? styles.activeItem : ""}`}
                onMouseEnter={() => setHoverIndex(index)}
                onMouseLeave={() => setHoverIndex(null)}
              >
                <Link
                  to={item.href}
                  className={`${styles.menuLink} ${activeItem === (item.key || item.name) ? styles.activeLink : ""}`}
                  onClick={(e) => handleMenuItemClick(item, e)}
                >
                  <div className={styles.menuIconBox}>
                    <div className={styles.menuIcon}>
                      {item.icon}
                      <div className={styles.iconRipple}></div>
                    </div>
                    {hoverIndex === index && activeItem !== (item.key || item.name) && <div className={styles.hoverEffect}></div>}
                    {activeItem === (item.key || item.name) && <div className={styles.activeEffect}></div>}
                  </div>

                  <span className={styles.menuText}>{item.name}</span>

                  {item.badge && (
                    <div className={styles.badgeWrapper}>
                      <span className={styles.badge}>{item.badge}</span>
                    </div>
                  )}

                  {item.hasSubmenu && (
                    <span className={styles.chevronContainer} onClick={(e) => toggleMenu(item.name, e)}>
                      <div className={`${styles.chevronBox} ${expandedMenus[item.name] ? styles.chevronExpanded : ""}`}>
                        <ChevronRight size={14} className={styles.chevron} />
                      </div>
                    </span>
                  )}
                </Link>

                {item.hasSubmenu && (
                  <ul className={`${styles.submenu} ${expandedMenus[item.name] ? styles.submenuExpanded : ""}`}>
                    {item.submenuItems &&
                      item.submenuItems.map((subItem, subIndex) => (
                        <li key={subIndex} className={styles.submenuItem}>
                          <Link
                            to={subItem.href}
                            className={`${styles.submenuLink} ${
                              location.pathname === subItem.href ? styles.activeSubmenuLink : ""
                            }`}
                            onClick={() => {
                              navigate(subItem.href)

                              if (isMobile) {
                                setSidebarOpen(false)
                              }
                            }}
                          >
                            <span className={styles.submenuDot}></span>
                            <span className={styles.submenuText}>{subItem.name}</span>
                          </Link>
                        </li>
                      ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.sidebarFooter}>
          <div className={styles.footerContent}>
            <Link
              to="/cixis"
              className={styles.logoutButton}
              onClick={(e) => {
                e.preventDefault()
                setShowLogoutConfirm(true)
              }}
            >
              <div className={styles.logoutIcon}>
                <LogOut size={18} />
              </div>
              <span className={styles.logoutText}>{t("common.logout")}</span>
            </Link>
          </div>
        </div>

        {isMobile && sidebarOpen && (
          <button className={styles.closeButton} onClick={toggleSidebar}>
            <X size={24} />
          </button>
        )}
      </div>

      {isMobile && sidebarOpen && <div className={styles.overlay} onClick={toggleSidebar}></div>}

      {showLogoutConfirm && (
        <div className={styles.logoutPopupOverlay}>
          <div id="logout-popup" className={styles.logoutPopup}>
            <div className={styles.logoutPopupHeader}>
              <div className={styles.logoutPopupIcon}>
                <AlertCircle size={20} />
              </div>
              <h3>{t("common.logoutConfirmTitle")}</h3>
            </div>
            <div className={styles.logoutPopupContent}>
              <p>{t("common.logoutConfirmMessage")}</p>
            </div>
            <div className={styles.logoutPopupActions}>
              <button className={styles.logoutPopupCancelButton} onClick={handleCancelLogout}>
                {t("common.cancel")}
              </button>
              <button className={styles.logoutPopupConfirmButton} onClick={handleConfirmLogout}>
                {t("common.logoutConfirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
